import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { createFileRoute } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"
import {
  ArrowRight,
  Book,
  Check,
  Code,
  Copy,
  Globe,
  Key,
  Package,
  Shield,
  Terminal,
  Webhook,
} from "lucide-react"
import { useState } from "react"

export const Route = createFileRoute("/docs")({
  component: DocsPage,
})

const DOC_SECTIONS = [
  {
    icon: Book,
    title: "Getting Started",
    description: "Set up your account, get API keys, and make your first payment in minutes.",
    badge: "Start here",
    badgeColor: "bg-blue-light text-blue",
    links: ["Quickstart guide", "Account setup", "Test your first payment", "Go live checklist"],
  },
  {
    icon: Code,
    title: "API Reference",
    description: "Complete reference for all endpoints, parameters, and response schemas.",
    badge: "Reference",
    badgeColor: "bg-surface text-muted",
    links: ["Payments API", "Merchants API", "Settlements API", "Webhooks API"],
  },
  {
    icon: Package,
    title: "SDKs & Libraries",
    description: "Official client libraries for Node.js, Python, Go, Ruby, PHP, and Java.",
    badge: "Popular",
    badgeColor: "bg-green-light text-green-DEFAULT",
    links: ["Node.js SDK", "Python SDK", "Go SDK", "Ruby SDK"],
  },
  {
    icon: Webhook,
    title: "Webhooks",
    description: "Real-time event delivery for every payment lifecycle state change.",
    badge: "Events",
    badgeColor: "bg-amber-light text-amber-DEFAULT",
    links: ["Event types", "Payload schemas", "Signature verification", "Retry behaviour"],
  },
  {
    icon: Key,
    title: "Authentication",
    description: "API key management, HMAC signatures, and OAuth2 flows.",
    badge: "Security",
    badgeColor: "bg-rose-light text-rose-DEFAULT",
    links: ["API keys", "HMAC signing", "OAuth2 tokens", "Key rotation"],
  },
  {
    icon: Terminal,
    title: "Sandbox",
    description: "Test cards, bank details, and simulated error scenarios for every edge case.",
    badge: "Testing",
    badgeColor: "bg-blue-light text-blue",
    links: ["Test cards", "Simulate declines", "Webhook testing", "Sandbox vs live"],
  },
  {
    icon: Globe,
    title: "Internationalisation",
    description: "Multi-currency, multi-language, and region-specific payment method guidance.",
    badge: "Global",
    badgeColor: "bg-green-light text-green-DEFAULT",
    links: ["Currency support", "Local methods", "FX conversion", "Country guides"],
  },
  {
    icon: Shield,
    title: "Security & Compliance",
    description: "PCI DSS scope reduction, data handling, and compliance documentation.",
    badge: "Compliance",
    badgeColor: "bg-amber-light text-amber-DEFAULT",
    links: ["PCI scope", "Data residency", "Audit logs", "Security FAQ"],
  },
]

const SDK_TABS = [
  {
    label: "Node.js",
    install: "npm install @orchestrate/node",
    filename: "payment.ts",
    code: `import Orchestrate from '@orchestrate/node'

const client = new Orchestrate({
  apiKey: process.env.ORCHESTRATE_API_KEY,
})

const payment = await client.payments.create({
  amount: 10000,        // amount in cents
  currency: 'USD',
  merchant_id: 'mer_abc123',
  payment_method: {
    type: 'card',
    card: {
      number: '4242424242424242',
      exp_month: 12,
      exp_year: 2027,
      cvc: '123',
    },
  },
  metadata: { order_id: 'ord_xyz' },
})

console.log(payment.id)      // pay_01HXYZ...
console.log(payment.status)  // 'succeeded'`,
  },
  {
    label: "Python",
    install: "pip install orchestrate-python",
    filename: "payment.py",
    code: `import orchestrate
import os

client = orchestrate.Client(
    api_key=os.environ["ORCHESTRATE_API_KEY"]
)

payment = client.payments.create(
    amount=10000,
    currency="USD",
    merchant_id="mer_abc123",
    payment_method={
        "type": "card",
        "card": {
            "number": "4242424242424242",
            "exp_month": 12,
            "exp_year": 2027,
            "cvc": "123",
        },
    },
    metadata={"order_id": "ord_xyz"},
)

print(payment.id)      # pay_01HXYZ...
print(payment.status)  # succeeded`,
  },
  {
    label: "Go",
    install: "go get github.com/orchestrate/orchestrate-go",
    filename: "payment.go",
    code: `package main

import (
  "fmt"
  "os"
  orchestrate "github.com/orchestrate/orchestrate-go"
)

func main() {
  client := orchestrate.NewClient(
    orchestrate.WithAPIKey(os.Getenv("ORCHESTRATE_API_KEY")),
  )

  payment, err := client.Payments.Create(ctx,
    &orchestrate.PaymentCreateParams{
      Amount:     10000,
      Currency:   "USD",
      MerchantID: "mer_abc123",
      PaymentMethod: &orchestrate.PaymentMethodParams{
        Type: "card",
        Card: &orchestrate.CardParams{
          Number:   "4242424242424242",
          ExpMonth: 12,
          ExpYear:  2027,
          CVC:      "123",
        },
      },
    },
  )
  if err != nil {
    panic(err)
  }

  fmt.Println(payment.ID)     // pay_01HXYZ...
  fmt.Println(payment.Status) // succeeded
}`,
  },
]

const WEBHOOK_EVENTS = [
  {
    event: "payment.succeeded",
    description: "A payment was successfully completed",
    severity: "success",
  },
  { event: "payment.failed", description: "A payment attempt failed", severity: "error" },
  {
    event: "payment.pending",
    description: "A payment is awaiting confirmation",
    severity: "pending",
  },
  {
    event: "chargeback.created",
    description: "A chargeback was filed by the cardholder",
    severity: "error",
  },
  {
    event: "settlement.completed",
    description: "Funds have been settled to the merchant",
    severity: "success",
  },
  {
    event: "merchant.approved",
    description: "Merchant onboarding was approved",
    severity: "success",
  },
  { event: "merchant.suspended", description: "Merchant account was suspended", severity: "error" },
  { event: "refund.created", description: "A refund was initiated", severity: "pending" },
]

const SEVERITY_STYLES: Record<string, string> = {
  success: "bg-green-light text-green-DEFAULT",
  error: "bg-rose-light text-rose-DEFAULT",
  pending: "bg-amber-light text-amber-DEFAULT",
}

interface CodeBlockProps {
  code: string
  filename: string
}

function CodeBlock({ code, filename }: CodeBlockProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    void navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0d1117] shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/10 bg-white/5 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5" aria-hidden="true">
            <div className="bg-rose-DEFAULT/60 h-3 w-3 rounded-full" />
            <div className="bg-amber-DEFAULT/60 h-3 w-3 rounded-full" />
            <div className="bg-green-DEFAULT/60 h-3 w-3 rounded-full" />
          </div>
          <span className="font-mono text-xs text-white/40">{filename}</span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-white/40 transition-colors hover:text-white/80"
          aria-label="Copy code to clipboard"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-6 font-mono text-sm leading-relaxed text-white/80">
        <code>{code}</code>
      </pre>
    </div>
  )
}

function DocsPage() {
  const [activeSDK, setActiveSDK] = useState(0)

  useSEO({
    title: "Documentation",
    description:
      "Complete Orchestrate API documentation, SDK guides, webhook reference, and integration tutorials.",
    keywords: "orchestrate API docs, payment SDK, webhook events, API reference, sandbox testing",
    canonical: "https://orchestrate.global/docs",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-ink relative overflow-hidden pt-32 pb-20">
        <div
          className="grid-bg pointer-events-none absolute inset-0 opacity-20"
          aria-hidden="true"
        />
        <div
          className="bg-blue/10 pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection>
                <SectionLabel label="Documentation" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                  Build with
                  <br />
                  <em className="text-blue-mid not-italic">confidence.</em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="mb-8 text-xl leading-relaxed text-white/50">
                  Everything you need to integrate Orchestrate — from your first API call to
                  production scale.
                </p>
                <div className="flex gap-3">
                  <Button variant="primary" size="lg">
                    Quickstart <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button variant="white-outline" size="lg">
                    API Reference
                  </Button>
                </div>
              </AnimatedSection>
            </div>

            <AnimatedSection direction="right" delay={0.2}>
              <CodeBlock
                filename="payment.ts"
                code={`import Orchestrate from '@orchestrate/node'

                    const client = new Orchestrate({
                      apiKey: process.env.ORCHESTRATE_API_KEY,
                    })

                    const payment = await client.payments.create({
                      amount: 10000,
                      currency: 'USD',
                      merchant_id: 'mer_abc123',
                      payment_method: { type: 'card', ... },
                    })

                    // pay_01HXYZ... | succeeded`}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Doc section grid */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-14 text-center">
              <SectionLabel label="Guides & References" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Find what you need
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {DOC_SECTIONS.map((sec, i) => {
              const Icon = sec.icon
              return (
                <motion.div
                  key={sec.title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true, amount: 0.08 }}
                >
                  <div className="border-border hover:shadow-ink/8 hover:border-blue-mid group h-full cursor-default rounded-2xl border bg-white p-5 transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl">
                    <div className="mb-4 flex items-start justify-between">
                      <div className="bg-blue-light group-hover:bg-blue-mid flex h-10 w-10 items-center justify-center rounded-xl transition-colors">
                        <Icon className="text-blue h-4 w-4" />
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${sec.badgeColor}`}
                      >
                        {sec.badge}
                      </span>
                    </div>
                    <h3 className="mb-1.5 text-sm font-semibold">{sec.title}</h3>
                    <p className="text-muted mb-3 text-xs leading-relaxed">{sec.description}</p>
                    <ul className="space-y-1">
                      {sec.links.map((link) => (
                        <li key={link}>
                          <button className="text-muted hover:text-blue group/link flex items-center gap-1 text-xs transition-colors">
                            <ArrowRight className="h-3 w-3 -translate-x-1 opacity-0 transition-all group-hover/link:translate-x-0 group-hover/link:opacity-100" />
                            {link}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* SDK examples with tabs */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-start gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection>
                <SectionLabel label="SDK Examples" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h2 className="font-display mb-6 text-4xl tracking-tight sm:text-5xl">
                  Your language
                  <br />
                  <em className="text-blue not-italic">of choice.</em>
                </h2>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="text-muted mb-8 text-lg leading-relaxed">
                  Official SDKs for every major language, maintained by Orchestrate engineers and
                  kept in sync with every API release.
                </p>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div
                  className="mb-5 flex flex-wrap gap-2"
                  role="tablist"
                  aria-label="SDK language tabs"
                >
                  {SDK_TABS.map((tab, i) => (
                    <button
                      key={tab.label}
                      role="tab"
                      aria-selected={activeSDK === i}
                      onClick={() => setActiveSDK(i)}
                      className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-150 ${
                        activeSDK === i
                          ? "bg-blue border-blue text-white"
                          : "text-muted border-border hover:border-blue-mid hover:text-ink bg-white"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2 flex items-center justify-between rounded-xl bg-[#0d1117] px-4 py-3">
                  <code className="text-green-DEFAULT font-mono text-sm">
                    $ {SDK_TABS[activeSDK].install}
                  </code>
                  <Copy className="h-3.5 w-3.5 cursor-pointer text-white/30 transition-colors hover:text-white/70" />
                </div>
                <p className="text-muted text-xs">
                  All SDKs are open source and available on{" "}
                  <span className="text-blue font-medium">GitHub</span>.
                </p>
              </AnimatedSection>
            </div>

            <AnimatedSection direction="right" delay={0.2}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSDK}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <CodeBlock
                    filename={SDK_TABS[activeSDK].filename}
                    code={SDK_TABS[activeSDK].code}
                  />
                </motion.div>
              </AnimatePresence>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Webhook events table */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-12">
              <SectionLabel label="Webhooks" className="mb-5" />
              <h2 className="font-display mb-4 text-4xl tracking-tight sm:text-5xl">
                Real-time events
              </h2>
              <p className="text-muted max-w-xl text-lg leading-relaxed">
                Subscribe to lifecycle events and react to payment state changes instantly. All
                events delivered with HMAC-SHA256 signatures.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <div className="border-border overflow-hidden rounded-2xl border bg-white">
              <div className="border-border bg-surface flex items-center justify-between border-b px-6 py-4">
                <span className="text-sm font-semibold">Event types</span>
                <span className="text-muted font-mono text-xs">API version: 2026-02-01</span>
              </div>
              <div className="divide-border divide-y">
                {WEBHOOK_EVENTS.map((ev, i) => (
                  <motion.div
                    key={ev.event}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: i * 0.04 }}
                    viewport={{ once: true }}
                    className="hover:bg-surface flex items-center justify-between px-6 py-4 transition-colors"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <code className="text-blue bg-blue-light flex-shrink-0 rounded-lg px-2.5 py-1 font-mono text-sm whitespace-nowrap">
                        {ev.event}
                      </code>
                      <span className="text-muted hidden truncate text-sm sm:block">
                        {ev.description}
                      </span>
                    </div>
                    <span
                      className={`ml-3 flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${SEVERITY_STYLES[ev.severity]}`}
                    >
                      {ev.severity}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
